/**
 * Rewrite Raptor feed teaser links for the current US siteaccess/host.
 *
 * Feeds keep global paths (/en/..., /de/...). On US views only, strip the
 * global language segment and apply the US prefix (/us_market, /de, or '').
 */

const GLOBAL_LANGUAGE_SEGMENTS = new Set([
    'en', 'de', 'pl', 'fr', 'es', 'da', 'hu', 'it', 'cn',
]);

/**
 * @param {string} href
 * @param {{ marketFilter?: string, usUrlPrefix?: string|null }} options
 * @returns {string}
 */
export function rewriteRaptorTeaserLink(href, options = {}) {
    const marketFilter = (options.marketFilter || '').trim();
    if (marketFilter !== 'us') {
        return href;
    }

    if (href == null || typeof href !== 'string') {
        return href;
    }

    const trimmed = href.trim();
    if (trimmed === '' || trimmed === '#' || trimmed.startsWith('#')) {
        return href;
    }

    const usUrlPrefix = options.usUrlPrefix == null ? '' : String(options.usUrlPrefix);

    let pathname;
    let search = '';
    let hash = '';

    if (trimmed.startsWith('/') && !trimmed.startsWith('//')) {
        const hashIdx = trimmed.indexOf('#');
        const withHash = hashIdx === -1 ? trimmed : trimmed.slice(0, hashIdx);
        hash = hashIdx === -1 ? '' : trimmed.slice(hashIdx);
        const qIdx = withHash.indexOf('?');
        pathname = qIdx === -1 ? withHash : withHash.slice(0, qIdx);
        search = qIdx === -1 ? '' : withHash.slice(qIdx);
    } else {
        try {
            const url = new URL(trimmed, typeof window !== 'undefined' ? window.location.href : 'https://example.invalid');
            if (url.protocol !== 'http:' && url.protocol !== 'https:') {
                return href;
            }
            if (typeof window !== 'undefined' && url.host !== window.location.host) {
                return href;
            }
            pathname = url.pathname || '/';
            search = url.search || '';
            hash = url.hash || '';
        } catch {
            return href;
        }
    }

    if (usUrlPrefix !== ''
        && (pathname === usUrlPrefix || pathname.startsWith(`${usUrlPrefix}/`))
    ) {
        return `${pathname}${search}${hash}`;
    }

    const remainder = stripGlobalLanguagePrefix(pathname);
    const rewrittenPath = joinUsPrefix(usUrlPrefix, remainder);

    return `${rewrittenPath}${search}${hash}`;
}

/**
 * @param {string} pathname
 * @returns {string}
 */
function stripGlobalLanguagePrefix(pathname) {
    const parts = pathname.split('/').filter(Boolean);
    if (parts.length > 0 && GLOBAL_LANGUAGE_SEGMENTS.has(parts[0])) {
        parts.shift();
    }
    return parts.length === 0 ? '/' : `/${parts.join('/')}`;
}

/**
 * @param {string} prefix
 * @param {string} path
 * @returns {string}
 */
function joinUsPrefix(prefix, path) {
    if (!prefix) {
        return path.startsWith('/') ? path : `/${path}`;
    }

    if (!path || path === '/') {
        return prefix;
    }

    const normalized = path.startsWith('/') ? path : `/${path}`;

    return `${prefix}${normalized}`;
}

// InstantSearch templates in Twig cannot ES-import; they call this from inline scripts.
if (typeof window !== 'undefined') {
    window.rewriteRaptorTeaserLink = rewriteRaptorTeaserLink;
}
